import { useCallback, useRef, useState } from "react";
import { TRPCClientError, type TRPCClientErrorLike } from "@trpc/client";
import type { AppRouter } from "@remi/api";
import { RnCopy } from "@remi/icons/next";
import { remiLogger } from "@remi/logging";
import { useEffect } from "@remi/next-ui";

import { copyHtmlToClipboard, copyTextToClipboard, copyTextToClipboardAsync } from "~/utils/clipboard";
import { displayToast } from "~/components/ui/sonner";

const COPIED_RESET_MS = 2000;
const DEFAULT_SUCCESS = "Copied to clipboard";
const DEFAULT_ERROR = "Failed to copy";

interface ToastConfig {
  loading?: string;
  success?: string;
  successDescription?: string;
  // The function form receives the input promise's rejection; clipboard-write failures fall
  // back to clipboardError.
  error?: string | ((reason: unknown) => string);
  errorDescription?: string;
  clipboardError?: string;
}

interface CopyOpts {
  silent?: boolean;
  toast?: ToastConfig;
}

const reportAsyncCopyFailure = (error: unknown, cause: "input" | "clipboard"): void => {
  // A tRPC error carrying server data was already seen by the tRPC handler, which either reported it or
  // deliberately suppressed it; capturing the client's copy re-opens what the server closed.
  const serverData: TRPCClientErrorLike<AppRouter>["data"] | undefined =
    error instanceof TRPCClientError ? error.data : undefined;
  if (serverData) {
    remiLogger.captureCounterMetric("clipboard.async-copy.server-answered-failure", 1, {
      attributes: { cause, errorType: serverData.errorType ?? serverData.code },
    });
    return;
  }
  remiLogger.error("Async clipboard write failed.", { error, attributes: { cause } });
};

export const useCopyToClipboard = () => {
  const [copied, setCopied] = useState(false);
  const [lastCopiedValue, setLastCopiedValue] = useState<string | null>(null);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const asyncInFlightRef = useRef(false);

  useEffect(
    () => () => {
      if (resetTimerRef.current !== null) {
        clearTimeout(resetTimerRef.current);
      }
    },
    [],
  );

  const markCopied = useCallback((value: string) => {
    setCopied(true);
    setLastCopiedValue(value);
    if (resetTimerRef.current !== null) {
      clearTimeout(resetTimerRef.current);
    }
    resetTimerRef.current = setTimeout(() => {
      setCopied(false);
      setLastCopiedValue(null);
      resetTimerRef.current = null;
    }, COPIED_RESET_MS);
  }, []);

  const copy = useCallback(
    async (text: string, opts?: CopyOpts): Promise<boolean> => {
      try {
        await copyTextToClipboard(text);
        markCopied(text);
        showSuccess(opts);
        return true;
      } catch (error) {
        remiLogger.error("Clipboard write failed.", { error });
        showError(opts, DEFAULT_ERROR);
        return false;
      }
    },
    [markCopied],
  );

  const copyAsync = useCallback(
    async (textPromise: Promise<string>, opts?: CopyOpts): Promise<boolean> => {
      if (asyncInFlightRef.current) {
        return false;
      }
      asyncInFlightRef.current = true;

      const loadingId = opts?.toast?.loading ? displayToast({ title: opts.toast.loading, variant: "info" }) : null;

      try {
        await copyTextToClipboardAsync(textPromise);
        const text = await textPromise;
        markCopied(text);
        showSuccess(opts, loadingId);
        return true;
      } catch (error) {
        const [inputResult] = await Promise.allSettled([textPromise]);
        const cause = inputResult.status === "rejected" ? "input" : "clipboard";
        reportAsyncCopyFailure(error, cause);
        const configuredError = opts?.toast?.error;
        const stringError = typeof configuredError === "string" ? configuredError : undefined;
        const inputError =
          inputResult.status === "rejected" && typeof configuredError === "function"
            ? configuredError(inputResult.reason)
            : (stringError ?? DEFAULT_ERROR);
        const message = cause === "input" ? inputError : (opts?.toast?.clipboardError ?? stringError ?? DEFAULT_ERROR);
        showError(opts, message, loadingId);
        return false;
      } finally {
        asyncInFlightRef.current = false;
      }
    },
    [markCopied],
  );

  const copyHtml = useCallback(
    async (html: string, text: string, opts?: CopyOpts): Promise<boolean> => {
      try {
        await copyHtmlToClipboard(html, text);
        markCopied(text);
        showSuccess(opts);
        return true;
      } catch (error) {
        remiLogger.error("Clipboard HTML write failed.", { error });
        showError(opts, DEFAULT_ERROR);
        return false;
      }
    },
    [markCopied],
  );

  const isCopied = useCallback((value: string) => lastCopiedValue === value, [lastCopiedValue]);

  return { copy, copyAsync, copyHtml, copied, lastCopiedValue, isCopied };
};

function showSuccess(opts: CopyOpts | undefined, replaceId: string | number | null = null): void {
  if (opts?.silent) {
    return;
  }
  displayToast({
    title: opts?.toast?.success ?? DEFAULT_SUCCESS,
    description: opts?.toast?.successDescription,
    variant: "secondary",
    icon: <RnCopy />,
    id: replaceId ?? undefined,
    duration: 3000,
  });
}

function showError(opts: CopyOpts | undefined, message: string, replaceId: string | number | null = null): void {
  if (opts?.silent) {
    return;
  }
  displayToast({
    title: message,
    description: opts?.toast?.errorDescription,
    variant: "error",
    id: replaceId ?? undefined,
  });
}
